import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import {
  AllPolicyStatusesData,
  AllPolicyStatusesVars,
  IResUpdatePolicyApi,
} from '../../../types/policies'
import {parseId, hasPermission} from '../../../helpers'
import Select from 'react-select'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'
import { getBootstrapColorHex, ALL_PERMISSIONS, STATUS_POLICY } from '../../../helpers/consts'
import { useGetUser } from '../../../store/userStore'
import {DateInput} from 'app/tfi/components/DateInput'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  statusPolicy: any
  loadingPoliciesStatus
  currentStatus?: string
  currentDocumentStatus?: string
  currentPaymentStatus?: string
  currentDocumentsDueDate?: string | null
  isQuotePolicy?: boolean
}

export const ModalStatusesPolicy: React.FC<IModalProps> = ({
  id,
  isOpen,
  onClose,
  refetch,
  statusPolicy,
  loadingPoliciesStatus,
  currentStatus = '',
  currentDocumentStatus = '',
  currentPaymentStatus = '',
  currentDocumentsDueDate = '',
  isQuotePolicy = false,
}) => {
  // Obtener el usuario actual, sus grupos y permisos
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const permissions = useGetUser((s) => s.user?.permissions)
  const isAsesor = userGroups.includes('Asesor')
  const canMarkAsCompleted = permissions && hasPermission(permissions, ALL_PERMISSIONS.MARK_COMPLETED_POLICY)

  // Si tiene el permiso MARK_COMPLETED_POLICY, puede editar todo sin restricciones
  const hasRestrictedAccess = isAsesor && !canMarkAsCompleted

  // Status codes permitidos para Asesores sin permiso especial
  const allowedStatusCodesForAsesor = ['NEW', 'RENEWED', 'PROCESADO']

  // Función para verificar si un status code es permitido para Asesor
  const isStatusAllowedForAsesor = (code: string): boolean => {
    // Verificar si es uno de los códigos exactos permitidos
    if (allowedStatusCodesForAsesor.includes(code)) {
      return true
    }
    // Verificar si comienza con UPDATED_BY_CLIENT
    if (code.startsWith('UPDATED_BY_CLIENT')) {
      return true
    }
    return false
  }

  // const {
  //   data: dataPoliciesStatuses,
  //   loading: loadingPoliciesStatus,
  //   error: errorPoliciesStatuses,
  // } = useQuery<AllPolicyStatusesData, AllPolicyStatusesVars>(GET_ALL_POLICIES_STATUSES, {
  //   fetchPolicy: 'cache-first',
  // })

  // Filtrar los status según el rol del usuario
  const allStatusOptions =
    statusPolicy?.allPolicyStatuses.edges.map(({node}) => ({
      value: node.code,
      label: node.title,
      category: node.category,
      color: getBootstrapColorHex(node.color || 'gray'),
      icon: <i className='bi bi-circle-fill' style={{color: getBootstrapColorHex(node.color || 'gray')}}></i>,
    })) || []

  // Si es Asesor sin permiso especial, filtrar solo los status permitidos
  const statusOptions = hasRestrictedAccess
    ? allStatusOptions.filter((option) => isStatusAllowedForAsesor(option.value))
    : allStatusOptions

  const customSingleValue = ({data}: any) => (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
      {data.icon}
      {data.label}
    </div>
  )

  const customOption = (props: any) => {
    const {data, innerRef, innerProps} = props
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{display: 'flex', alignItems: 'center', padding: 10}}
      >
        {data.icon}
        <span style={{marginLeft: 8}}>{data.label}</span>
      </div>
    )
  }

  const [formData, setFormData] = useState({
    statusPolicy: '',
    documentStatus: '',
    documentsDueDate: '',
    paymentStatus: '',
  })

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate, error: errorUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  const selectedStatusCategory =
    allStatusOptions.find((option) => option.value === formData.statusPolicy)?.category || null
  const shouldRequireDocumentsDueDate =
    !hasRestrictedAccess &&
    formData.documentStatus === 'PENDING' &&
    selectedStatusCategory === STATUS_POLICY.COMPLETED

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success('Status updated successfully')
      refetch()
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(`Error ${dataUpdate.updatePolicy.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleDateChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (shouldRequireDocumentsDueDate && !formData.documentsDueDate) {
      toast.error('Documents due date is required when document status is Pending and policy status is Completed')
      return
    }

    const policyData: Record<string, any> = {
      ...(!isQuotePolicy && formData.statusPolicy ? {status: formData.statusPolicy} : {}),
      ...(formData.documentStatus ? {stateDocs: formData.documentStatus} : {}),
      ...(formData.paymentStatus ? {paymentStatus: formData.paymentStatus} : {}),
    }

    if (!hasRestrictedAccess && formData.documentStatus) {
      policyData.documentsDueDate =
        formData.documentStatus === 'PENDING' ? formData.documentsDueDate || null : null
    }

    gqlCreateP({
      variables: {
        id: parseId(id),
        policyData,
      },
    })
  }

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        statusPolicy: '',
        documentStatus: '',
        documentsDueDate: '',
        paymentStatus: '',
      })
      return
    }

    setFormData({
      statusPolicy: currentStatus || '',
      documentStatus: currentDocumentStatus || '',
      documentsDueDate: currentDocumentsDueDate || '',
      paymentStatus: currentPaymentStatus || '',
    })
  }, [isOpen, currentStatus, currentDocumentStatus, currentDocumentsDueDate, currentPaymentStatus])

  return (
    <Modal show={isOpen} onHide={onClose} className='modal-statuss-policy'>
      <Modal.Header closeButton>
        <Modal.Title>Policy Statuses</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formId' onSubmit={handleSubmit}>
          {!isQuotePolicy && (
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Select
                className='selectable'
                options={statusOptions}
                value={statusOptions.find((opt) => opt.value === formData.statusPolicy)}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    statusPolicy: selected?.value || '',
                  }))
                }
                isSearchable={true}
                filterOption={(option, inputValue) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
                components={{SingleValue: customSingleValue, Option: customOption}}
                styles={{
                  control: (base) => ({...base, minHeight: 40}),
                  option: (base, {data, isFocused}) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f0f0f0' : 'white',
                    color: data.color,
                  }),
                }}
                placeholder='Select one'
                isDisabled={loadingPoliciesStatus}
              />
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>Documents status</Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  documentStatus: e.target.value,
                  documentsDueDate: e.target.value === 'PENDING' ? prev.documentsDueDate : '',
                }))
              }
              value={formData.documentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>Select one</option>
              <option value='ACTIVE'>Active</option>
              <option value='PENDING'>Pending</option>
              <option value='PROCESSING'>Processing</option>
              <option value='DECLINED'>Declined</option>
              <option value='COMPLETED'>Completed</option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                You don't have permissions to modify document status
              </Form.Text>
            )}
          </Form.Group>
          {formData.documentStatus === 'PENDING' && (
            <Form.Group className='mt-2'>
              <DateInput
                id='documentsDueDate'
                name='documentsDueDate'
                label='Documents Due Date'
                value={formData.documentsDueDate}
                onChange={handleDateChange}
                disabled={hasRestrictedAccess}
                required={shouldRequireDocumentsDueDate}
              />
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>Payment status</Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentStatus: e.target.value,
                }))
              }
              value={formData.paymentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>Select one</option>
              <option value='PAID'>Paid</option>
              <option value='FAILED'>Failed</option>
              <option value='PENDING'>Pending</option>
              <option value='AUTO_PAY'>Auto Pay</option>
              <option value='NOT_APPLICABLE'>Not Applicable ($0)</option>
              <option value='PAGA_POR_SU_CUENTA'>Paga por su cuenta</option> 
              <option value='CHARGEBACK'>Chargeback</option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                You don't have permissions to modify payment status
              </Form.Text>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='primary'
          type='submit'
          form='formId'
          formAction='formId'
          disabled={loadingUpdate}
        >
          {loadingUpdate && <Spinner animation='border' size='sm' className='me-2' />}
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
